import { isDatePast } from 'app/lib/date';
import type { ResponseWithSignals } from 'app/network/signals';
import { isFuture } from 'date-fns';
import type { Channel } from './chat';
import type { Group } from './group';
import type { Listing, Offer, Request } from './listing';
import type { PostResponse } from './post';
import type { Profile } from './profile';
import type { User } from './user';

export enum LoanStatus {
  Accepted = 'accepted',
  Completed = 'completed',
  Pending = 'pending',
  Rejected = 'rejected',
  Returned = 'returned',
}

export enum LoanType {
  Borrowing = 'borrowing',
  Lending = 'lending',
}

/**
 * Rating categories supported by the backend (mirrors the `rating_type_enum`
 * in the API). Each category is rated 1-5 by the lender and/or borrower.
 */
export enum RatingType {
  AssetQuality = 'asset_quality',
  LogisticsReliability = 'logistics_reliability',
  CommunityTrust = 'community_trust',
}

export interface RateLoanPayload {
  rating?: number;
  ratings?: { type: RatingType; rating: number }[];
  comment?: string;
}

export interface Loan extends ResponseWithSignals {
  borrower: User & { profile?: Profile; memberships?: { group: Pick<Group, 'name' | 'id' | 'picture'> }[] };
  borrowerId: User['id'];
  createdAt: string;
  end: string;
  id: string;
  lender: User & { profile?: Profile; memberships?: { group: Pick<Group, 'name' | 'id' | 'picture'> }[] };
  lenderId: User['id'];
  listing: Offer | Request;
  listingId: string;
  proposer: User & { profile?: Profile };
  proposerId: User['id'];
  start: string;
  status: LoanStatus;
  updatedAt?: string;
  channelURL: Channel['channel_url'];
  accepted?: string;
  returned?: string;
  isUpdateFor?: string; // Is a replacement for another loan, eg. when requesting updated dates
  rating?: {
    id: string;
    lenderRating?: number | null;
    borrowerRating?: number | null;
    lenderAssetQualityRating?: number | null;
    lenderLogisticsReliabilityRating?: number | null;
    lenderCommunityTrustRating?: number | null;
    borrowerAssetQualityRating?: number | null;
    borrowerLogisticsReliabilityRating?: number | null;
    borrowerCommunityTrustRating?: number | null;
    comment?: string | null;
    createdAt: string;
    updatedAt?: string;
  };
  posts?: PostResponse[];
  fee?: number | null;
  feeDescription?: string | null;
  feePaymentMethod?: string | null;
  numParticipants?: number | null;
  flexibleDates?: boolean;
  participants?: { user: Loan['lender']; numParticipants: number; channelURL: Channel['channel_url'] }[] | null;
}

export interface CreateLoanDTO {
  channelURL?: Channel['channel_url'];
  listingId?: Listing['id'];
  otherUserId?: User['id'];
  end: Date;
  start: Date;
  isUpdateFor?: Loan['id'];
  fee?: number | null;
  feeDescription?: string | null;
  feePaymentMethod?: string | null;
  flexibleDates?: boolean;
}

export interface CreateLoanParticipationDTO {
  listingId: Listing['id'];
  numParticipants: number;
}

export interface FindAllLoansDTO {
  status?: Loan['status'][];
  type?: LoanType | LoanType[];
}

export enum LoanETA {
  pending = 'pending',
  scheduled = 'scheduled',
  late = 'late',
  ongoing = 'ongoing',
  returned = 'returned', // awaiting lender confirmation
  past = 'past',
}
export const getLoanETA = ({ start, end, status }: Pick<Loan, 'start' | 'end' | 'status'>) =>
  status === LoanStatus.Completed
    ? LoanETA.past
    : status === LoanStatus.Returned
      ? LoanETA.returned
      : status === LoanStatus.Pending
        ? LoanETA.pending
        : isFuture(new Date(start))
          ? LoanETA.scheduled
          : isDatePast(new Date(end))
            ? LoanETA.late
            : LoanETA.ongoing;

export const getLoanType = (borrower?: User, currentUser?: User): LoanType =>
  borrower?.id === currentUser?.id ? LoanType.Borrowing : LoanType.Lending;

export const getLoanTypeForUser = (loan: Pick<Loan, 'borrowerId' | 'lenderId'>, currentUserId?: User['id']) => {
  if (!currentUserId) {
    return null;
  }

  if (loan.borrowerId === currentUserId) {
    return LoanType.Borrowing;
  }

  if (loan.lenderId === currentUserId) {
    return LoanType.Lending;
  }

  return null;
};

export const getCurrentUserLoanRating = (
  loan: Pick<Loan, 'borrowerId' | 'lenderId' | 'rating'>,
  currentUserId?: User['id'],
) => {
  const loanType = getLoanTypeForUser(loan, currentUserId);

  if (loanType === LoanType.Borrowing) {
    return loan.rating?.borrowerRating;
  }

  if (loanType === LoanType.Lending) {
    return loan.rating?.lenderRating;
  }

  return undefined;
};

export const hasCurrentUserRatedLoan = (
  loan: Pick<Loan, 'borrowerId' | 'lenderId' | 'rating'>,
  currentUserId?: User['id'],
) => typeof getCurrentUserLoanRating(loan, currentUserId) === 'number';

export const isLoanPendingCurrentUserRating = (
  loan: Pick<Loan, 'borrowerId' | 'lenderId' | 'rating' | 'status'>,
  currentUserId?: User['id'],
) =>
  loan.status === LoanStatus.Completed &&
  Boolean(getLoanTypeForUser(loan, currentUserId)) &&
  !hasCurrentUserRatedLoan(loan, currentUserId);

export const canUserRateLoan = (
  loan: Pick<Loan, 'borrowerId' | 'lenderId' | 'rating' | 'status'>,
  currentUserId?: User['id'],
) => isLoanPendingCurrentUserRating(loan, currentUserId);

export const canUserCompleteLoan = (loan: Pick<Loan, 'status'>, isLender: boolean) =>
  loan.status === LoanStatus.Accepted || (loan.status === LoanStatus.Returned && isLender);
